import type { APIUser } from 'Models/models';
import type { RoleName } from 'Backend/user-management/utils/roleName';

import { roleTypes } from 'Pages/Team/utils/craftedRoles';
import isEqual from 'utils/isEqual';
import { noPermissionsV2 } from 'Backend/user-management/utils/noPermissionsV2';

/** Checks the given permissions and maps them to a role name. */
export const mapPermissionsToRoleName = (user: Pick<APIUser, 'permissions' | 'roleName'>) => {
    let roleName = 'Custom' as RoleName;

    const craftPermissions = mergePermissions(user?.permissions);

    roleTypes.forEach((role) => {
        if (isEqual(craftPermissions, role?.Permissions)) {
            roleName = role?.Name;
        }
    });

    // If user has matching owner permissions, but is not an owner, return Custom role
    if (roleName === 'Owner' && user?.roleName !== 'Owner') {
        roleName = 'Custom';
    }

    // If user has the Owner roleName, return Owner role
    if (user?.roleName === 'Owner') {
        return 'Owner';
    }

    return roleName;
};

/** Uses noPermissionsV2 (which is typed from APICompanyPermissions.yml) and merges it with the user's permissions. Needed to fill any missing permissions e.g. if a user has `cards: { manage: 0 }` and is missing `cards.view` */
const mergePermissions = (userPermissions?: APIUser['permissions']): APIUser['permissions'] => ({
    beneficiaries: {
        ...noPermissionsV2.beneficiaries,
        ...userPermissions?.beneficiaries,
    },
    cards: {
        ...noPermissionsV2.cards,
        ...userPermissions?.cards,
    },
    company: {
        ...noPermissionsV2.company,
        ...userPermissions?.company,
    },
    conversions: {
        ...noPermissionsV2.conversions,
        ...userPermissions?.conversions,
    },
    payments: {
        ...noPermissionsV2.payments,
        ...userPermissions?.payments,
    },
    users: {
        ...noPermissionsV2.users,
        ...userPermissions?.users,
    },
    approval: {
        ...noPermissionsV2.approval,
        ...userPermissions?.approval,
    },
    payroll: {
        ...noPermissionsV2.payroll,
        ...userPermissions?.payroll,
    },
    billing: {
        ...noPermissionsV2.billing,
        ...userPermissions?.billing,
    },
    bills: {
        ...noPermissionsV2.bills,
        ...userPermissions?.bills,
    },
});
